<script setup lang="ts">
import type { ContentModelText } from '~/models/Content/ContentModelText'
import { computed, useTemplateRef } from 'vue'
import { faArrowLeft, faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { KsIcon } from '@aschehoug/kloss'
import { ArticleSize } from '~/models/Content/ContentArticle'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'

defineProps<{
  content: ContentModelText
  size: ArticleSize
}>()

const richtext = useTemplateRef('richtext')
const tags = computed(() => richtext.value?.querySelectorAll('[data-group]') ?? [])
</script>

<template>
  <div
    class="au-model-text grid grid-cols-subgrid rounded-xl border-4 border-[--theme-10] bg-white p-8 pl-20 shadow-[8px_8px_0_var(--theme-pop)]"
    :class="{ 'au-model-text-is-child-level': content.viewMode === 'child_level' }"
  >
    <div
      class="relative"
      :class="size === ArticleSize.Medium ? 'col-[5/-5]' : 'col-[4/-4]'"
    >
      <Teleport
        v-for="(el, index) of tags"
        :key="index"
        :to="el"
        defer
      >
        <label class="absolute top-0 grid size-11 translate-x-[calc(100%*-1-1rem)] place-items-center rounded-full border-2 border-[--theme-pop] bg-[--theme-pop] text-base text-[--theme-60] hover:border-[--theme-10] hover:bg-[--theme-10] has-[:checked]:!border-[--theme-60] has-[:focus-visible]:ring">
          <input
            type="checkbox"
            class="sr-only"
          >
          <KsIcon
            :icon="faArrowRight"
            class="[input:checked~&]:hidden"
          />
          <KsIcon
            :icon="faArrowLeft"
            class="[input:not(:checked)~&]:hidden"
          />
        </label>
      </Teleport>

      <div ref="richtext" class="au-prose">
        <RichTextRenderer :text="content.text" />
      </div>
    </div>
  </div>
</template>

<style scoped>
  .au-model-text-is-child-level :deep(.tag) {
  display: block;
  position: relative;

  &:has(> label input:checked) {
    background-color: var(--theme-10);

    &::before {
      display: inline-block;
      content: attr(data-group);
      background-color: var(--theme-60);
      border: 2px solid var(--theme-10);
      color: var(--theme-pop);
      border-radius: 50%;
      aspect-ratio: 1;
      overflow: hidden;
      display: grid;
      place-items: center;
      width: calc(var(--col-width) * 4);
      position: absolute;
      left: -2rem;
      font-size: 1.5rem;
      top: calc(2.75rem / 2); /* tailwind's size-11 / 2 */
      transform: translateY(-50%);
      text-align: center;
      padding: 1rem;
    }
  }
}

.au-model-text:not(.au-model-text-is-child-level) :deep(.tag) {
  display: block;
  position: relative;

  &:has(> label input:checked) {
    background-color: var(--theme-10);

    &::before {
      display: inline-block;
      content: attr(data-group);
      background-color: var(--theme-60);
      color: var(--theme-pop);
      border-end-end-radius: 10rem;
      border-start-end-radius: 10rem;
      padding-inline: 0.8em;
      transform: translateX(-0.8em);
      box-shadow: calc(var(--col-gap) * -1 + 0.8em) 0 0 var(--theme-60);
    }
  }
}
</style>
